import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

const eurFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "EUR",
  minimumFractionDigits: 2,
})

export function formatEur(amount: number): string {
  return eurFormatter.format(amount)
}

export function formatMinorUnits(value: string): string {
  const num = parseFloat(value)
  if (isNaN(num)) return value
  return eurFormatter.format(num / 100)
}

export function formatShortDate(dateStr: string): string {
  const date = parseApiDate(dateStr)
  if (!date) return dateStr
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

export function parseApiTimestamp(value?: string): Date | undefined {
  if (!value) return undefined
  const hasExplicitTimezone = /(Z|[+-]\d{2}:?\d{2})$/i.test(value)
  const dateTimeWithoutTimezone =
    /^\d{4}-\d{2}-\d{2}[T\s]\d{2}:\d{2}/.test(value) && !hasExplicitTimezone
  const timestamp = dateTimeWithoutTimezone
    ? `${value.replace(" ", "T")}Z`
    : value
  const date = new Date(timestamp)
  return Number.isNaN(date.getTime()) ? undefined : date
}

export function parseApiDate(value?: string): Date | undefined {
  if (!value) return undefined
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split("-").map(Number)
    return new Date(year, month - 1, day)
  }
  return parseApiTimestamp(value)
}

export function formatApiDateTime(value?: string): string {
  const date = parseApiTimestamp(value)
  if (!date) return "Never"
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date)
}

// An events report's window is UTC; its zone (UTC when the request named none) is only how
// the report is presented, so the window is shown in that zone rather than the viewer's.
export function formatReportPeriodBoundary(
  value: string,
  reportType: string | undefined,
  timeZone?: string,
): string {
  const normalizedReportType = reportType?.toLowerCase()
  if (
    normalizedReportType === "events" ||
    normalizedReportType === "events_report"
  ) {
    return formatInstantInZone(value, timeZone || "UTC")
  }
  return formatShortDate(value)
}

function formatInstantInZone(value: string, timeZone: string): string {
  const date = parseApiTimestamp(value)
  if (!date) return value
  try {
    const formatted = new Intl.DateTimeFormat(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
      timeZone,
    }).format(date)
    return `${formatted} (${timeZone})`
  } catch {
    // An unknown zone: fall back to UTC rather than to the viewer's own zone.
    return formatInstantInZone(value, "UTC")
  }
}
